import { lazy } from 'react';

import { LayoutType, PageType } from '@shared/constants/constants';
import {
  ACTION_BACK_PATH,
  MRRP_MEDICAL_REPORTS_COLLECTION_PATH,
  MRRP_MEDICAL_REPORTS_DETAILS_PATH,
  MRRP_MEDICAL_REPORTS_LISTING_PATH,
  MRRP_MEDICAL_REPORTS_NEW_SELECT_INSTITUTION_PATH,
  MRRP_MEDICAL_REPORTS_PARTICULARS_PATH,
  MRRP_MEDICAL_REPORTS_PATH,
  MRRP_MEDICAL_REPORTS_PATIENT_NOT_FOUND_PATH,
  MRRP_MEDICAL_REPORTS_REPORTS_DETAILS_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_COMPLETED_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_DD_RECEIPT_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_STATUS_PATH,
  MRRP_MEDICAL_REPORTS_REQUEST_SUMMARY_PATH,
  MRRP_MEDICAL_REPORTS_TNC_DETAILS_PATH,
  MRRP_MEDICAL_REPORTS_TNC_PATH,
  MRRP_MEDICAL_REPORTS_VIEW_SELECT_INSTITUTION_PATH,
} from '@shared/constants/paths';
import { SERVICE_CATALOG_CODE } from '@shared/constants/serviceCatalog';
import type { RouteConfig } from '@shared/types/common.types';

// Landing
const MedicalReportLandingPage = lazy(() => import('./pages/MedicalReportLandingPage'));

// Listing
const RequestedMedicalReportsPage = lazy(() => import('./pages/RequestedMedicalReportsPage'));
const MedicalReportDetailsPage = lazy(() => import('./pages/MedicalReportDetailsPage'));

// New Request
const SelectInstitutionPage = lazy(() => import('./pages/SelectInstitutionPage'));
const TnCPage = lazy(() => import('./pages/TnCPage'));
const TnCDetailsPage = lazy(() => import('./pages/TnCDetailsPage'));
const PatientNotFoundPage = lazy(() => import('./pages/PatientNotFoundPage'));
const ReportDetailsPage = lazy(() => import('./pages/ReportDetailsPage'));
const ParticularsPage = lazy(() => import('./pages/ParticularsPage'));
const CollectionPage = lazy(() => import('./pages/CollectionPage'));
const RequestSummaryPage = lazy(() => import('./pages/RequestSummaryPage'));

// Payment
const RequestPaymentPage = lazy(() => import('./pages/RequestPaymentPage'));
const RequestPaymentStatusPage = lazy(() => import('./pages/RequestPaymentStatusPage'));
const PaymentDDReceiptPage = lazy(() => import('./pages/PaymentDDReceiptPage'));
const PaymentCompletedPage = lazy(() => import('./pages/PaymentCompletedPage'));

export const MRRP_ROUTES: RouteConfig[] = [
  {
    actionUrl: ACTION_BACK_PATH,
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <MedicalReportLandingPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MRRP_MEDICAL_REPORTS_PATH,
    component: () => <SelectInstitutionPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_VIEW_SELECT_INSTITUTION_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <RequestedMedicalReportsPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_LISTING_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <MedicalReportDetailsPage />,
    displayTitle: () => 'Medical Report Details',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_DETAILS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MRRP_MEDICAL_REPORTS_PATH,
    component: () => <SelectInstitutionPage isNew />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_NEW_SELECT_INSTITUTION_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <TnCPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_TNC_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <TnCDetailsPage />,
    displayTitle: () => 'Terms and Conditions',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_TNC_DETAILS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <PatientNotFoundPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_PATIENT_NOT_FOUND_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <ReportDetailsPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_REPORTS_DETAILS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <ParticularsPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_PARTICULARS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <CollectionPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_COLLECTION_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <RequestSummaryPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_REQUEST_SUMMARY_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <RequestPaymentPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <RequestPaymentStatusPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_STATUS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.COMMON],
    component: () => <PaymentDDReceiptPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PUBLIC,
    path: MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_DD_RECEIPT_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MRRP_MEDICAL_REPORTS_PATH,
    component: () => <PaymentCompletedPage />,
    displayTitle: () => 'Medical Reports / Certificates Requests',
    pageType: PageType.PRIVATE,
    path: MRRP_MEDICAL_REPORTS_REQUEST_PAYMENT_COMPLETED_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicalReportsCertificatesRequests,
  },
];
